import { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import {
  generateV2ServiceCategoryOptions,
  getAncestors,
  getDescendants,
} from "../utils/serviceCategoryV2Mapping";

/**
 * Standalone V2 grouped Service Category dropdown.
 * Mirrors the look & feel of the dropdown in components/Provider/VendorFilters.jsx.
 *
 * Controlled component:
 *   - selected: Array<{ value: rfi_id, label: rfi_name }>
 *   - onChange: (next) => void   // receives the new array (same shape)
 *   - rfiList: raw RFI list from useRfiListForFilters / UseGetRfiListForFilters
 *   - lockedValue: optional rfi_id that cannot be removed (e.g. Foundation - All)
 *   - placeholder: optional placeholder when nothing selected
 */
export default function ServiceCategoryV2Dropdown({
  selected = [],
  onChange,
  rfiList = [],
  lockedValue = null,
  placeholder = "Service Categories",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  const v2SCOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfiList || []),
    [rfiList]
  );

  const filteredSCOptions = useMemo(() => {
    if (!searchQuery.trim()) return v2SCOptions;
    const q = searchQuery.toLowerCase();
    const matchingGroups = new Set();
    v2SCOptions.forEach((opt) => {
      if (!opt.isGroupHeader && opt.label.toLowerCase().includes(q)) {
        matchingGroups.add(opt.groupName);
      }
    });
    return v2SCOptions.filter((opt) => {
      if (opt.isGroupHeader) return matchingGroups.has(opt.label);
      return opt.label.toLowerCase().includes(q);
    });
  }, [v2SCOptions, searchQuery]);

  // Name -> rfi_id lookup
  const scNameToId = useMemo(() => {
    const map = {};
    (v2SCOptions || []).forEach((o) => {
      if (!o.isGroupHeader && o.rfiId) map[o.value] = o.rfiId;
    });
    return map;
  }, [v2SCOptions]);

  const makeSelection = (name) => ({
    value: scNameToId[name] || name,
    label: name,
  });

  // Dedupe by label so duplicate RFI names (e.g. "Wearables (All Types)")
  // never produce two badges or two API entries
  const dedupeByLabel = (arr) => {
    const seen = new Set();
    const result = [];
    for (const item of arr || []) {
      const key = (item?.label ?? item?.value ?? "").toString().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
    return result;
  };

  const emit = (next) => {
    if (onChange) onChange(dedupeByLabel(next));
  };

  const handleToggle = (value) => {
    const opt = v2SCOptions.find((o) => o.value === value);
    if (!opt) return;
    if (opt.isGroupHeader) {
      const members = opt.groupMembers || [];
      const memberIds = members.map((m) => scNameToId[m]).filter(Boolean);
      const allSelected = memberIds.every((id) =>
        selected.some((sc) => sc.value === id)
      );
      if (allSelected) {
        const memberIdSet = new Set(memberIds);
        // Don't remove the locked value
        emit(
          selected.filter(
            (sc) => !memberIdSet.has(sc.value) || sc.value === lockedValue
          )
        );
      } else {
        const existing = new Set(selected.map((sc) => sc.value));
        const toAdd = members
          .filter((m) => !existing.has(scNameToId[m]))
          .map((m) => makeSelection(m));
        emit([...selected, ...toAdd]);
      }
      return;
    }
    const optId = scNameToId[value];
    if (optId === lockedValue) return; // can't toggle locked
    const isSelected = selected.some((sc) => sc.value === optId);
    if (isSelected) {
      emit(selected.filter((sc) => sc.value !== optId));
    } else {
      const namesToAdd = [value, ...getDescendants(value), ...getAncestors(value)];
      const existing = new Set(selected.map((sc) => sc.value));
      const newItems = namesToAdd
        .filter(
          (n) =>
            scNameToId[n] &&
            !existing.has(scNameToId[n]) &&
            v2SCOptions.some((o) => o.value === n)
        )
        .map((n) => makeSelection(n));
      emit([...selected, ...newItems]);
    }
  };

  const removeTag = (value) => {
    if (value === lockedValue) return;
    emit(selected.filter((sc) => sc.value !== value));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap items-center gap-1 min-h-[24px]">
          {selected.length > 0 ? (
            dedupeByLabel(selected)
              .filter((tag) => tag && (tag.value != null || tag.label != null))
              .map((tag) => {
                const tagValue =
                  typeof tag.value === "string" || typeof tag.value === "number"
                    ? tag.value
                    : "";
                const tagLabel =
                  typeof tag.label === "string"
                    ? tag.label
                    : typeof tag.value === "string"
                      ? tag.value
                      : "";
              const rfiItem = (rfiList ?? []).find(
                (r) => r.rfi_id === tagValue || r.rfi_name === tagValue
              );
              const bgColor = rfiItem?.rfi_color_code_bg || "#EEF2FF";
              const textColor = rfiItem?.rfi_color_code_text || "#4338CA";
              const borderColor = rfiItem?.rfi_color_code_border || "#4338CA";
              const isLocked = tagValue === lockedValue;
              return (
                <span
                  key={tagValue}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                  style={{
                    backgroundColor: bgColor,
                    color: textColor,
                    "--tw-ring-color": borderColor + "33",
                  }}
                >
                  {tagLabel}
                  {!isLocked && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTag(tagValue);
                      }}
                      className="ml-0.5 inline-flex items-center hover:opacity-70"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </span>
              );
              })
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="w-full rounded-md border-0 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 focus:outline-none"
                placeholder="Search categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div
            className="max-h-[26rem] overflow-y-auto py-1 px-3"
            style={{ paddingBottom: "1rem" }}
          >
            <ul className="space-y-0">
              {filteredSCOptions.map((option, idx) => {
                if (option.isGroupHeader) {
                  const members = option.groupMembers || [];
                  const selectedInGroup = members.filter((m) =>
                    selected.some(
                      (sc) => sc.value === scNameToId[m] || sc.label === m
                    )
                  );
                  const allSelected =
                    members.length > 0 && selectedInGroup.length === members.length;
                  const someSelected =
                    selectedInGroup.length > 0 && !allSelected;
                  return (
                    <li
                      key={`grp-${idx}`}
                      className="pt-3 pb-1 pl-3 first:pt-0 select-none"
                    >
                      <span
                        className={`text-[11px] font-semibold uppercase tracking-wider cursor-pointer transition-colors ${
                          allSelected || someSelected
                            ? "text-indigo-600"
                            : "text-gray-400 hover:text-gray-600"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(option.value);
                        }}
                      >
                        {option.label}
                      </span>
                    </li>
                  );
                }
                const indentPx = (option.depth || 0) * 16;
                const optId = scNameToId[option.value];
                const isChecked = selected.some((sc) => sc.value === optId);
                const rfiItem = (rfiList ?? []).find(
                  (r) => r.rfi_name === option.value
                );
                const dotColor = rfiItem?.rfi_color_code_text || null;
                return (
                  <li
                    key={`sc-${idx}`}
                    className={`relative select-none py-1 pr-9 cursor-pointer ${
                      isChecked ? "bg-indigo-50" : "hover:bg-indigo-50"
                    }`}
                    style={{ paddingLeft: `${12 + indentPx}px` }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(option.value);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {dotColor && (
                        <span
                          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: dotColor }}
                        />
                      )}
                      <span
                        className={`text-sm ${
                          isChecked ? "font-semibold" : "font-normal"
                        } text-gray-900`}
                      >
                        {option.label}
                      </span>
                    </div>
                    {isChecked && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <svg
                          className="h-5 w-5 text-indigo-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
